import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";

/**
 * Build Remittance Verification Subject
 *
 * Adapter that assembles the generic `verify_step_output` inputs for a
 * remittance advice: the extracted fields + line items as a plain `subject`,
 * the source `documentIds` for multimodal grounding, and the entity link
 * (`AR_REMITTANCE_ADVICE` + adviceId) so a failed check's escalation task is
 * attached to the remittance.
 *
 * The source document is taken from the live `document` reference passed in
 * (wired from `normalize_remittance_document.document`) and flattened to
 * `documentIds` — keeping the generic verify node's surface to a single
 * `documentIds` input rather than teaching it about document references.
 */
export const buildRemittanceVerificationSubjectStep = defineStepConfig({
  type: "build_remittance_verification_subject",
  name: "Build Remittance Verification Subject",
  description:
    "Assemble the verify_step_output inputs (subject + documentIds + entity link) for a remittance advice.",
  category: "Data Access",
  libraryModule: "AccountsReceivable",
  inputSchema: z.object({
    remittanceAdviceId: z
      .uuid()
      .describe("Remittance advice to assemble a verification subject for"),
    document: entityOfType(SystemEntity.CORE_DOCUMENT)
      .optional()
      .describe(
        "Source document to verify against (normalize_remittance_document passthrough); flattened to documentIds. Empty when the advice has no attached document.",
      ),
  }),
  outputSchema: z.object({
    subject: z
      .record(z.string(), z.unknown())
      .describe("Extracted remittance fields + line items to sanity-check"),
    documentIds: z
      .array(z.uuid())
      .describe(
        "Source document ids for multimodal grounding (empty when none); fed to verify_step_output.documentIds",
      ),
    sourceEntityType: z
      .nativeEnum(SystemEntity)
      .describe(
        "Entity type the escalation task links to (AR_REMITTANCE_ADVICE)",
      ),
    sourceEntityId: z
      .uuid()
      .describe("Entity id the escalation task links to (the advice id)"),
  }),
  configSchema: z.object({}),
  emits: [] as const,
});
